import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import { browserLocalPersistence, connectAuthEmulator, getAuth, setPersistence, type Auth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';

interface FirebaseClients {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
  functions: Functions;
  storage: FirebaseStorage;
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const useAuthEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_AUTH_EMULATOR === 'true';
const useFirestoreEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_FIRESTORE_EMULATOR === 'true';
const useFunctionsEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true';
const useStorageEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_STORAGE_EMULATOR === 'true';

function initializeFirebase(): FirebaseClients {
  const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

  const auth = getAuth(app);
  void setPersistence(auth, browserLocalPersistence);

  const firestore = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  });

  const storage = getStorage(app, firebaseConfig.storageBucket);

  const functionsRegion = import.meta.env.VITE_FIREBASE_LOCATION_ID || 'europe-north1';
  const functions = getFunctions(app, functionsRegion);

  if (useAuthEmulator) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  }
  if (useFirestoreEmulator) {
    connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
  }
  if (useFunctionsEmulator) {
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  }
  if (useStorageEmulator) {
    connectStorageEmulator(storage, '127.0.0.1', 9199);
  }

  return { app, auth, firestore, functions, storage };
}

const { app, auth, firestore, functions, storage } = initializeFirebase();

export { app, auth, firebaseConfig, firestore, functions, storage };
